import { ACTIVATION_LABELS } from '../classification';
import {
  clusterTopForVolume,
  EC2_INSTANCES_KEY,
  UNATTACHED_KEY,
  isDrillLocationEmpty,
} from '../drill/drillLocation';
import { clusterGrouping } from '../grouping/clusterGrouping';
import { regionGrouping } from '../grouping/regionGrouping';
import { tagGrouping } from '../grouping/tagGrouping';
import { volumeAccountKey } from '../grouping/groupingPathShared';
import { effectiveSecondary } from '../secondaryGrouping';
import type {
  DrillLocation,
  ExplorationSecondary,
  ExplorationVolume,
  StructuralLocation,
} from '../../types/exploration';

export interface AncestryChip {
  type: string;
  name: string;
}

export const FOCUSED_REASON_LABELS = new Set([
  ACTIVATION_LABELS.unsupported,
  ACTIVATION_LABELS.potentiallySupported,
]);

export const FOCUSED_REASON_KEYS = new Set(['unsupported', 'potentiallySupported']);

export interface DrillAncestryOptions {
  location: DrillLocation;
  secondary: ExplorationSecondary;
  groupByAccount: boolean;
}

export interface VolumeAncestryOptions {
  secondary: ExplorationSecondary;
  tagKey?: string;
  groupByAccount: boolean;
}

/** Structural-only chips (no status/reason overlay) for a cluster-shaped location. */
function chipsFromClusterStructural(s: Extract<StructuralLocation, { kind: 'cluster' }>): AncestryChip[] {
  const chips: AncestryChip[] = [];
  if (s.top.kind === 'ec2') {
    chips.push({ type: 'ec2', name: EC2_INSTANCES_KEY });
    if (s.instanceId) chips.push({ type: 'instance', name: s.instanceId });
    return chips;
  }
  if (s.top.kind === 'unattached') {
    chips.push({ type: 'unattached', name: UNATTACHED_KEY });
    return chips;
  }
  chips.push({ type: 'cluster', name: s.top.name });
  if (s.nodeGroup) chips.push({ type: 'nodeGroup', name: s.nodeGroup });
  if (s.instanceId) chips.push({ type: 'instance', name: s.instanceId });
  return chips;
}

/** Pure structural location (no status/activation overlay) — for the volume "Path" tooltip. */
function pureStructuralLocation(
  volume: ExplorationVolume,
  opts: VolumeAncestryOptions
): Pick<DrillLocation, 'account' | 'structural'> {
  const groupingSecondary = effectiveSecondary(opts.secondary, opts.tagKey ?? '');
  const pathOpts = { tagKey: opts.tagKey ?? '', groupByAccount: opts.groupByAccount, view: 'exploration' as const };

  if (volume.instanceId) {
    const model =
      groupingSecondary === 'region' ? regionGrouping : groupingSecondary === 'tag' ? tagGrouping : clusterGrouping;
    const location = model.locationForInstance(volume, volume.instanceId, pathOpts);
    if (location) return location;
  }

  const account = opts.groupByAccount ? volumeAccountKey(volume) : undefined;
  if (groupingSecondary === 'region') {
    return { account, structural: { kind: 'region', region: volume.region || '(unknown)' } };
  }
  if (groupingSecondary === 'tag') {
    const tagValue = (volume.tags && opts.tagKey && volume.tags[opts.tagKey]) || '(untagged)';
    return { account, structural: { kind: 'tag', tagValue } };
  }
  return { account, structural: { kind: 'cluster', top: clusterTopForVolume(volume) } };
}

/** Full structural ancestry for a volume tooltip Path section. */
export function ancestryChipsFromVolume(
  volume: ExplorationVolume,
  opts: VolumeAncestryOptions
): AncestryChip[] {
  const location = pureStructuralLocation(volume, opts);
  const chips: AncestryChip[] = [];
  if (opts.groupByAccount && location.account) chips.push({ type: 'account', name: location.account });
  const s = location.structural;
  if (s.kind === 'cluster') {
    chips.push(...chipsFromClusterStructural(s));
  } else if (s.kind === 'region') {
    if (s.region) chips.push({ type: 'region', name: s.region });
    if (s.instanceId) chips.push({ type: 'instance', name: s.instanceId });
  } else if (s.kind === 'tag') {
    if (s.tagValue) chips.push({ type: 'tag', name: s.tagValue });
    if (s.instanceId) chips.push({ type: 'instance', name: s.instanceId });
  }
  return chips;
}

/** Structural entities the current drill location has navigated through. */
export function currentDrillAncestryChips(opts: DrillAncestryOptions): AncestryChip[] {
  const { location } = opts;
  if (isDrillLocationEmpty(location)) return [];

  const accountChip: AncestryChip[] = [];
  if (opts.groupByAccount && location.account) {
    accountChip.push({ type: 'account', name: location.account });
  }

  const s = location.structural;
  // Drill ancestry for region/tag grouping only surfaces the top bucket.
  if (s.kind === 'region') {
    return s.region ? [...accountChip, { type: 'region', name: s.region }] : accountChip;
  }
  if (s.kind === 'tag') {
    return s.tagValue ? [...accountChip, { type: 'tag', name: s.tagValue }] : accountChip;
  }
  if (s.kind === 'cluster') {
    return [...accountChip, ...chipsFromClusterStructural(s)];
  }
  return accountChip;
}

/** Hierarchy type of the current drill location's tail (current breadcrumb item). */
export function currentDrillTailType(opts: { location: DrillLocation; secondary: ExplorationSecondary }): string {
  const { location, secondary } = opts;
  if (isDrillLocationEmpty(location)) return '';

  if (location.reasonLabel) return 'reason';
  if (location.statusLabel || location.focusedActivationKey) return 'status';

  const s = location.structural;
  if (s.kind === 'cluster') {
    if (s.instanceId) return 'instance';
    if (s.nodeGroup) return 'nodeGroup';
    if (s.top.kind === 'ec2') return 'ec2';
    if (s.top.kind === 'unattached') return 'unattached';
    return 'cluster';
  }
  if (s.kind === 'region') return s.instanceId ? 'instance' : 'region';
  if (s.kind === 'tag') return s.instanceId ? 'instance' : 'tag';
  if (secondary === 'none') return 'status';
  return location.account ? 'account' : '';
}

interface D3HierarchyNode {
  parent?: D3HierarchyNode | null;
  data?: { type?: string; name?: string };
}

/** Full ancestry for a hovered status/reason bubble tooltip Path breadcrumb. */
export function ancestryChipsForNode(
  d: D3HierarchyNode,
  opts: DrillAncestryOptions
): AncestryChip[] {
  const visible: AncestryChip[] = [];
  let p = d.parent;
  while (p) {
    const t = p.data?.type;
    if (t && t !== 'root' && t !== 'status' && t !== 'reason' && p.data?.name != null) {
      visible.unshift({ type: t, name: p.data.name });
    }
    p = p.parent;
  }

  if (isDrillLocationEmpty(opts.location)) return visible;

  return currentDrillAncestryChips(opts).concat(visible.slice(1));
}
